import moment from 'moment';
import rejectExpenseTemplate from './modals/reject-expense/reject-expense.html';
import RejectExpenseModalController from './modals/reject-expense/reject-expense.controller';
import markProcessedTemplate from './modals/mark-processed/mark-processed.html';
import MarkProcessedModalController from './modals/mark-processed/mark-processed.controller';
import {
  locationController,
  locationTemplate
} from '../../expenses/modals';

class ExpenseReviewDetailsController {

  constructor(CompanyService, toastr, $scope, $http, $rootScope, $stateParams, $state, $uibModal, TravelExpenseService, AuthenticationService) {
    'ngInject';

    this.moment = moment;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.toastr = toastr;
    this.state = $state;
    this.$stateParams = $stateParams;
    this.modal = $uibModal;
    this.apiHost = $rootScope.apiHost;
    this.travelExpenseService = TravelExpenseService;
    this.auth = AuthenticationService;
    this.$scope = $scope;
    this.receiptBusyKey = null;
    this.pendingReceiptAction = null;
    this.itineraryBusy = false;
    this.statusChangeBusy = false;
    this.selectedStatusChange = null;

    this.travelExpense = {
      expenses: []
    };

    this.statusOptions = {
      approveButtonText: 'Approve',
      approveNextStatus: 'APPROVED',
      rejectButtonText: 'Reject',
      rejectNextStatus: 'REJECTED',
    };

    this.travelExpenseStatusDetails = {};
    this.statusChangeOptions = [
      { value: 'APPROVED', label: 'Awaiting Documentation' },
      { value: 'REIMBURSEMENT_REQUESTED', label: 'Awaiting Reimbursement Approval' },
      { value: 'REIMBURSEMENT_REJECTED', label: 'Reimbursement Rejected' },
      { value: 'REIMBURSEMENT_APPROVED', label: 'Reimbursement Approved' }
    ];

    this.loading = true;
    this.travelExpenseService.getExpenseReport($stateParams.expenseReportId).then((response) => {
      const report = this.travelExpenseService.normalizeExpenseReportDates(response.data);
      this.travelExpenseService.hydrateEstimateBasisDisplay(report);
      this.travelExpenseService.hydrateMileageDisplay(report);
      this.travelExpense = report;
      this.travelExpense.expenses = report.expenses || [];
      this.travelExpenseStatusDetails = this.travelExpenseService.getTravelExpenseStatusDetails(this.travelExpense.status);
      this.selectedStatusChange = this.getDefaultStatusChange();
      this.loading = false;
    }, () => {
      this.loading = false;
      this.toastr.error('Could not load the expense report.', 'Error');
    });

    $scope.$watch(() => this.travelExpenseStatusDetails, (newVal) => {
      if (!newVal) {
        return;
      }

      if (newVal.value === 'REIMBURSEMENT_REQUESTED' || newVal.value === 'REIMBURSEMENT_APPROVED') {
        this.statusOptions.approveButtonText = 'Approve Reimbursement';
        this.statusOptions.approveNextStatus = 'REIMBURSEMENT_APPROVED';
        this.statusOptions.rejectButtonText = 'Reject Reimbursement';
        this.statusOptions.rejectNextStatus = 'REIMBURSEMENT_REJECTED';
      } else {
        this.statusOptions.approveButtonText = 'Approve';
        this.statusOptions.approveNextStatus = 'APPROVED';
        this.statusOptions.rejectButtonText = 'Reject';
        this.statusOptions.rejectNextStatus = 'REJECTED';
      }
    });
  }

  isBillableTrip() {
    return this.travelExpense.purpose === 'customer';
  }

  isPostTravel() {
    return this.travelExpenseStatusDetails && this.travelExpenseStatusDetails.phase === 'Post-Travel';
  }

  getAdditionalExpensesTotal() {
    return (this.travelExpense.expenses || [])
      .filter((expense) => !expense.itinerary)
      .reduce((total, expense) => total + (expense.amount || 0), 0);
  }

  getProjectLabel() {
    if (this.travelExpense.projectName) {
      return this.travelExpense.projectName;
    }

    if (this.travelExpense.project) {
      return String(this.travelExpense.project);
    }

    return '';
  }

  getPaymentMethodLabel(expense) {
    if (!expense) {
      return '—';
    }

    if (expense.type === 'Personally Owned Vehicle') {
      return 'Personal';
    }

    if (!expense.paymentMethod) {
      return '—';
    }

    if (expense.paymentMethod === 'corporate') {
      return 'Corporate';
    }

    if (expense.paymentMethod === 'personal') {
      return 'Personal';
    }

    return expense.paymentMethod;
  }

  canManageReceipts() {
    return this.auth && this.auth.hasPermission('PERM_MANAGE_EMPLOYEE_EXPENSES');
  }

  canManageItinerary() {
    return this.canManageReceipts();
  }

  canChangeStatus() {
    return this.canManageReceipts() && !!this.travelExpense.id;
  }

  getAvailableStatusChanges() {
    const current = (this.travelExpense.status || '').toUpperCase();
    return this.statusChangeOptions.filter((option) => option.value !== current);
  }

  getDefaultStatusChange() {
    const available = this.getAvailableStatusChanges();
    if ((this.travelExpense.status || '').toUpperCase() === 'PROCESSED') {
      // Re-open to approved so managers can proxy-edit without a rejection reason.
      const approved = available.find((option) => option.value === 'REIMBURSEMENT_APPROVED');
      if (approved) {
        return approved.value;
      }
    }
    return available.length ? available[0].value : null;
  }

  canEditAsProxy() {
    return this.canManageReceipts()
      && !!this.travelExpense.id
      && (this.travelExpense.status || '').toUpperCase() !== 'PROCESSED';
  }

  openActualsEditor() {
    if (!this.travelExpense.id) {
      return;
    }
    this.state.go('travel-expense-actuals', {
      expenseReportId: this.travelExpense.id,
      proxyImport: 'true'
    });
  }

  getItineraryLocations() {
    return (this.travelExpense.expenses || []).filter((expense) => expense.itinerary);
  }

  canRemoveLocation() {
    return this.getItineraryLocations().length > 1;
  }

  formatApiDate(value) {
    if (!value) {
      return null;
    }
    return this.moment(value).format('YYYY-MM-DD');
  }

  buildLocationPayload(location) {
    return {
      itinerary: true,
      place: location.place,
      zip: location.zip || null,
      fromDate: this.formatApiDate(location.fromDate),
      toDate: this.formatApiDate(location.toDate),
      daysTraveled: location.daysTraveled,
      mealsRate: location.mealsRate || 0,
      lodgingRate: location.lodgingRate || 0,
      type: location.type || null,
      description: location.description || ('Travel to ' + (location.place || 'destination')),
      amount: 0
    };
  }

  reloadExpenseReport(successMessage) {
    return this.travelExpenseService.getExpenseReport(this.travelExpense.id).then((response) => {
      const report = this.travelExpenseService.normalizeExpenseReportDates(response.data);
      this.travelExpenseService.hydrateEstimateBasisDisplay(report);
      this.travelExpenseService.hydrateMileageDisplay(report);
      this.travelExpense = report;
      this.travelExpense.expenses = report.expenses || [];
      this.travelExpenseStatusDetails = this.travelExpenseService.getTravelExpenseStatusDetails(this.travelExpense.status);
      this.selectedStatusChange = this.getDefaultStatusChange();
      if (successMessage) {
        this.toastr.success(successMessage, 'Updated');
      }
    }).finally(() => {
      this.itineraryBusy = false;
      this.statusChangeBusy = false;
      this.loading = false;
    });
  }

  applyStatusChange() {
    if (!this.canChangeStatus() || this.statusChangeBusy || !this.selectedStatusChange) {
      return;
    }

    const nextStatus = this.selectedStatusChange;
    if (nextStatus === (this.travelExpense.status || '').toUpperCase()) {
      return;
    }

    const isRejection = nextStatus === 'REJECTED' || nextStatus === 'REIMBURSEMENT_REJECTED';
    // Manager status changes should not prompt for a rejection reason.
    const reason = isRejection ? 'Reopened by manager for corrections' : null;

    this.statusChangeBusy = true;
    this.loading = true;
    this.travelExpenseService.updateExpenseReportStatus(
      this.travelExpense.id,
      nextStatus,
      reason,
      null,
      false // administrative status change — do not email the employee
    ).then(() => {
      return this.reloadExpenseReport('Status updated. Use Edit as Proxy to change the report.');
    }, (failure) => {
      this.statusChangeBusy = false;
      this.loading = false;
      const message = (failure && failure.data && failure.data.message)
        || 'Could not update status.';
      this.toastr.error(message, 'Error');
    });
  }

  openLocationModal(existingLocation) {
    if (!this.canManageItinerary() || this.itineraryBusy) {
      return;
    }

    const self = this;
    const modalInstance = this.modal.open({
      template: locationTemplate,
      controller: locationController,
      controllerAs: '$ctrl',
      resolve: {
        billableTrip() {
          return self.isBillableTrip();
        },
        existingLocation() {
          return existingLocation || null;
        }
      },
      windowClass: 'hmodal-success'
    });

    modalInstance.addLocationToItinerary = function(location) {
      self.itineraryBusy = true;
      self.loading = true;
      self.travelExpenseService.createExpenseAdmin(
        self.travelExpense.id,
        self.buildLocationPayload(location)
      ).then(() => {
        return self.reloadExpenseReport('Location added.');
      }, (failure) => {
        self.itineraryBusy = false;
        self.loading = false;
        const message = (failure && failure.data && failure.data.message)
          || 'Could not add location.';
        self.toastr.error(message, 'Error');
      });
    };

    modalInstance.updateLocationInItinerary = function(original, updated) {
      if (!original || !original.id) {
        return;
      }

      self.itineraryBusy = true;
      self.loading = true;
      const payload = self.buildLocationPayload(updated);
      self.travelExpenseService.updateExpenseAdmin(
        self.travelExpense.id,
        original.id,
        {
          place: payload.place,
          zip: payload.zip,
          fromDate: payload.fromDate,
          toDate: payload.toDate,
          daysTraveled: payload.daysTraveled,
          mealsRate: payload.mealsRate,
          lodgingRate: payload.lodgingRate,
          itinerary: true,
          description: payload.description
        }
      ).then(() => {
        return self.reloadExpenseReport('Itinerary updated.');
      }, (failure) => {
        self.itineraryBusy = false;
        self.loading = false;
        const message = (failure && failure.data && failure.data.message)
          || 'Could not update location.';
        self.toastr.error(message, 'Error');
      });
    };
  }

  addLocation() {
    this.openLocationModal(null);
  }

  editLocation(location) {
    this.openLocationModal(location);
  }

  removeLocation(location) {
    if (!this.canManageItinerary() || this.itineraryBusy || !location || !location.id) {
      return;
    }

    if (!this.canRemoveLocation()) {
      this.toastr.warning('At least one itinerary location is required.', 'Cannot Remove');
      return;
    }

    this.itineraryBusy = true;
    this.loading = true;
    this.travelExpenseService.deleteExpenseAdmin(this.travelExpense.id, location.id).then(() => {
      return this.reloadExpenseReport('Location removed.');
    }, (failure) => {
      this.itineraryBusy = false;
      this.loading = false;
      const message = (failure && failure.data && failure.data.message)
        || 'Could not remove location.';
      this.toastr.error(message, 'Error');
    });
  }

  receiptActionKey(expense, receipt) {
    const expenseId = expense && expense.id != null ? expense.id : 'new';
    const receiptId = receipt && (receipt.receipt || receipt.id) ? (receipt.receipt || receipt.id) : 'upload';
    return expenseId + ':' + receiptId;
  }

  isReceiptBusy(expense, receipt) {
    return this.receiptBusyKey === this.receiptActionKey(expense, receipt);
  }

  beginReplaceReceipt(expense, receipt) {
    if (!this.canManageReceipts() || !expense || !receipt || !receipt.receipt || this.receiptBusyKey) {
      return;
    }
    this.pendingReceiptAction = { mode: 'replace', expense, receipt };
    this.openAdminReceiptFilePicker();
  }

  beginUploadReceipt(expense) {
    if (!this.canManageReceipts() || !expense || this.receiptBusyKey) {
      return;
    }
    this.pendingReceiptAction = { mode: 'upload', expense, receipt: null };
    this.openAdminReceiptFilePicker();
  }

  openAdminReceiptFilePicker() {
    const input = document.getElementById('admin-receipt-file-input');
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }

  onAdminReceiptFileSelected(fileList) {
    const run = () => {
      const file = fileList && fileList.length ? fileList[0] : null;
      const pending = this.pendingReceiptAction;
      this.pendingReceiptAction = null;
      if (!file || !pending || !pending.expense) {
        return;
      }

      if (pending.mode === 'replace') {
        this.replaceReceipt(pending.expense, pending.receipt, file);
        return;
      }

      this.uploadReceipt(pending.expense, file);
    };

    if (this.$scope.$$phase) {
      run();
    } else {
      this.$scope.$apply(run);
    }
  }

  replaceReceipt(expense, receipt, file) {
    if (!file || !expense || !receipt || !receipt.receipt) {
      return;
    }
    const busyKey = this.receiptActionKey(expense, receipt);
    this.receiptBusyKey = busyKey;
    this.travelExpenseService.replaceExpenseReceipt(
      this.travelExpense.id,
      expense.id,
      receipt.receipt,
      file,
      receipt.receiptKind
    ).then((response) => {
      this.applyExpenseReceipts(expense, response.data);
      this.toastr.success('Receipt replaced.', 'Updated');
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message)
        || 'Could not replace the receipt.';
      this.toastr.error(message, 'Error');
    }).finally(() => {
      if (this.receiptBusyKey === busyKey) {
        this.receiptBusyKey = null;
      }
    });
  }

  uploadReceipt(expense, file) {
    if (!file || !expense) {
      return;
    }
    const busyKey = this.receiptActionKey(expense, null);
    this.receiptBusyKey = busyKey;
    this.travelExpenseService.uploadExpenseReceiptAdmin(
      this.travelExpense.id,
      expense.id,
      file
    ).then((response) => {
      this.applyExpenseReceipts(expense, response.data);
      this.toastr.success('Receipt uploaded.', 'Updated');
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message)
        || 'Could not upload the receipt.';
      this.toastr.error(message, 'Error');
    }).finally(() => {
      if (this.receiptBusyKey === busyKey) {
        this.receiptBusyKey = null;
      }
    });
  }

  applyExpenseReceipts(expense, updatedExpense) {
    if (!expense || !updatedExpense) {
      return;
    }
    expense.receipts = updatedExpense.receipts || [];
    this.travelExpenseService.hydrateEstimateBasisDisplay(this.travelExpense);
  }

  cancel() {
    this.state.go('admin.expenses');
  }

  isLocked() {
    return !this.travelExpenseStatusDetails.editable;
  }

  approveExpenseReport() {
    this.loading = true;
    this.travelExpenseService.updateExpenseReportStatus(this.travelExpense.id, this.statusOptions.approveNextStatus).then(() => {
      this.loading = false;
      this.toastr.success('Expense report approved.', 'Approved');
      this.state.go('admin.expenses');
    }, () => {
      this.loading = false;
      this.toastr.error('Could not approve the expense report.', 'Error');
    });
  }

  rejectExpenseReport() {
    this.showRejectExpenseDialog().then((rejectionReason) => {
      if (!rejectionReason) {
        return;
      }

      this.loading = true;
      this.travelExpenseService.updateExpenseReportStatus(
        this.travelExpense.id,
        this.statusOptions.rejectNextStatus,
        rejectionReason
      ).then(() => {
        this.loading = false;
        this.toastr.success('Expense report rejected.', 'Rejected');
        this.state.go('admin.expenses');
      }, () => {
        this.loading = false;
        this.toastr.error('Could not reject the expense report.', 'Error');
      });
    });
  }

  markExpenseReportAsProcessed() {
    this.showMarkProcessedDialog().then((result) => {
      if (!result || !result.processedDate) {
        return;
      }

      this.loading = true;
      this.travelExpenseService.updateExpenseReportStatus(
        this.travelExpense.id,
        'PROCESSED',
        null,
        result.processedDate,
        result.notify
      ).then(() => {
        this.loading = false;
        this.toastr.success('Expense report marked as processed.', 'Processed');
        this.state.go('admin.expenses');
      }, () => {
        this.loading = false;
        this.toastr.error('Could not mark the expense report as processed.', 'Error');
      });
    });
  }

  showRejectExpenseDialog() {
    const isReimbursement = this.statusOptions.rejectNextStatus === 'REIMBURSEMENT_REJECTED';

    return this.modal.open({
      template: rejectExpenseTemplate,
      controller: RejectExpenseModalController,
      controllerAs: '$ctrl',
      windowClass: 'hmodal-danger',
      resolve: {
        modalContext: () => ({
          reportName: this.travelExpense.name,
          isReimbursement
        })
      }
    }).result.then((reason) => reason, () => undefined);
  }

  showMarkProcessedDialog() {
    const reimbursementAmount = this.travelExpenseService.formatNumber(
      this.travelExpenseService.getEmployeeReimbursementTotal(this.travelExpense)
    );
    const totalCost = this.travelExpenseService.formatNumber(
      this.travelExpenseService.getTotalForPhase(this.travelExpense, this.travelExpenseStatusDetails.phase)
    );

    return this.modal.open({
      template: markProcessedTemplate,
      controller: MarkProcessedModalController,
      controllerAs: '$ctrl',
      windowClass: 'hmodal-success',
      resolve: {
        modalContext: () => ({
          reportName: this.travelExpense.name,
          reimbursementAmount,
          totalCost
        })
      }
    }).result.then((result) => result, () => undefined);
  }
}

export default ExpenseReviewDetailsController;



// WEBPACK FOOTER //
// ./client/pages/admin/expense-review-details/expense-review-details.controller.js